/* ============================================================
   Koigenix — global app boot.
   - Lenis smooth scroll (gated on reduced-motion / coarse perf)
   - GSAP ScrollTrigger sync
   - IntersectionObserver scroll-reveals (no scroll listeners)
   - Smooth anchor scrolling
   Per-section behavior lives in its own /assets module.
   ============================================================ */
(function () {
  'use strict';

  var docEl = document.documentElement;
  docEl.classList.remove('no-js');
  docEl.classList.add('js');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasGsap = typeof window.gsap !== 'undefined';
  var hasScrollTrigger = hasGsap && typeof window.ScrollTrigger !== 'undefined';
  if (hasScrollTrigger) window.gsap.registerPlugin(window.ScrollTrigger);

  /* ---- Lenis smooth scroll ---- */
  var lenis = null;
  function initLenis() {
    if (reduceMotion || typeof window.Lenis === 'undefined') return;
    lenis = new window.Lenis({
      duration: 1.1,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      touchMultiplier: 1.6
    });
    if (hasScrollTrigger) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      window.gsap.ticker.lagSmoothing(0);
    } else {
      function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
      requestAnimationFrame(raf);
    }
  }

  /* ---- Reveal on scroll ---- */
  function initReveals() {
    var els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- Generic count-up for stats ([data-countup="42"], optional
     data-countup-dec / -pre / -suf) ---- */
  function initCountUps() {
    var els = document.querySelectorAll('[data-countup]');
    if (!els.length) return;
    function run(el) {
      var to = parseFloat(el.getAttribute('data-countup'));
      var dec = parseInt(el.getAttribute('data-countup-dec') || '0', 10);
      var pre = el.getAttribute('data-countup-pre') || '';
      var suf = el.getAttribute('data-countup-suf') || '';
      if (reduceMotion || !window.gsap) { el.textContent = pre + to.toFixed(dec) + suf; return; }
      var o = { v: 0 };
      window.gsap.to(o, { v: to, duration: 1.1, ease: 'power2.out', onUpdate: function () { el.textContent = pre + o.v.toFixed(dec) + suf; } });
    }
    if (reduceMotion || !('IntersectionObserver' in window)) { els.forEach(run); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { run(e.target); io.unobserve(e.target); } });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- Generic accordion: [data-acc-trigger] (button) + aria-controls panel
     with a max-height transition. Used by What's Inside, Analysis, FAQ. ---- */
  function initAccordions() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-acc-trigger]');
      if (!btn) return;
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      var open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!open));
      if (open) {
        panel.style.maxHeight = panel.scrollHeight + 'px';
        requestAnimationFrame(function () { panel.style.maxHeight = '0px'; });
      } else {
        panel.style.maxHeight = panel.scrollHeight + 'px';
        panel.addEventListener('transitionend', function te() { panel.style.maxHeight = 'none'; panel.removeEventListener('transitionend', te); });
      }
    });
  }

  /* ---- Smooth anchor scroll (uses Lenis when present) ---- */
  function initAnchors() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href], [data-scroll-to]');
      if (!a) return;
      var raw = a.getAttribute('data-scroll-to') || a.getAttribute('href') || '';
      var hi = raw.indexOf('#');
      if (hi < 0) return;
      var hash = raw.slice(hi);
      if (hash.length < 2) return;
      var node;
      try { node = document.querySelector(hash); } catch (err) { return; }
      if (!node) return; // target isn't on this page — let the browser navigate (e.g. to the homepage)
      e.preventDefault();
      if (lenis) lenis.scrollTo(node, { offset: -70, duration: 1.2 });
      else node.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    });
  }

  /* ---- Public hooks for section modules ----
     Merge onto window.Koi so the cart config injected by theme.liquid
     (Koi.cart, Koi.initialCart) is preserved. ---- */
  var Koi = window.Koi = window.Koi || {};
  Koi.reduceMotion = reduceMotion;
  Koi.hasGsap = hasGsap;
  Koi.hasScrollTrigger = hasScrollTrigger;
  Koi.refreshReveals = initReveals; // re-scan reveals after editor re-render
  Object.defineProperty(Koi, 'lenis', { get: function () { return lenis; }, configurable: true });

  function boot() { initLenis(); initReveals(); initCountUps(); initAnchors(); initAccordions(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme-editor: re-init reveals + count-ups when a section is re-rendered
  document.addEventListener('shopify:section:load', function () { initReveals(); initCountUps(); });
})();
